import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Card, CardContent, Container, Stack } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '@/components/common/PageHeader';

import { PasswordField } from '../components/PasswordField';
import { authService } from '../services/authService';
import { changePasswordSchema, type ChangePasswordFormValues } from '../schemas';

export function ChangePasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
  });

  async function handleFormSubmit(values: ChangePasswordFormValues): Promise<void> {
    setSubmitError(null);
    setSuccess(false);
    try {
      await authService.changePassword(values.oldPassword, values.newPassword);
      setSuccess(true);
      reset();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Error al cambiar la contraseña');
    }
  }

  return (
    <Container maxWidth="sm">
      <PageHeader title="Cambiar contraseña" />
      <Card>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
            <Stack spacing={2}>
              {success && (
                <Alert severity="success">Contraseña actualizada correctamente.</Alert>
              )}
              {submitError && <Alert severity="error">{submitError}</Alert>}

              <PasswordField
                label="Contraseña actual"
                fullWidth
                autoComplete="current-password"
                {...register('oldPassword')}
                error={!!errors.oldPassword}
                helperText={errors.oldPassword?.message}
              />

              <PasswordField
                label="Nueva contraseña"
                fullWidth
                autoComplete="new-password"
                {...register('newPassword')}
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message}
              />

              <PasswordField
                label="Confirmar nueva contraseña"
                fullWidth
                autoComplete="new-password"
                {...register('confirmPassword')}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
              />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={() => navigate(-1)}>Cancelar</Button>
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {isSubmitting ? 'Cambiando...' : 'Cambiar contraseña'}
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}
